import React, { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchInfinite, fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { Button } from '@dashboard/components/ui/button';
import Spinner from '@dashboard/components/Spinner/Spinner';
import Dash from '@dashboard/components/Dash';
import { Badge } from '@dashboard/components/ui/badge';
import { DataGrid } from '@dashboard/components/DataGrid';
import { format } from 'date-fns';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { stringify } from 'query-string';
import { integrationEventsKey } from '@dashboard/lib/queryKeys';

function getJsonResponse(response) {
  try {
    return JSON.stringify(JSON.parse(response), null, 2);
  } catch {
    return response;
  }
}

function getStatusBadgeAppearance(deliveryStatus) {
  switch (deliveryStatus) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'pending':
    default:
      return 'info';
  }
}

function ResendButton({ log }) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const resendIntegrationMutation = useMutation({
    mutationFn: async () =>
      fetcher(`/integrations/${log.integrationId}/logs/${log._id}/resend`, token, {
        method: 'POST',
      }),
    onSuccess: result => {
      if (result.statusCode === 200) {
        toast({ title: 'Event resent' });
      } else {
        toast({ title: 'Resend failed', type: 'error' });
      }
    },
    onError: () => {
      toast({ title: 'Resend failed', type: 'error' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: integrationEventsKey(log.integrationId) });
    },
  });

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={event => {
        event.stopPropagation();
        resendIntegrationMutation.mutate();
      }}
      disabled={resendIntegrationMutation.isLoading}
    >
      Resend
      {resendIntegrationMutation.isLoading && <Spinner className="!text-current" />}
    </Button>
  );
}

export function EventDetails({ log }) {
  const response = getJsonResponse(log.response);

  return (
    <div className="px-4 py-3 text-sm">
      <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2">
        <div>HTTP status code</div>
        <div>{log.statusCode}</div>

        {log.apiKeyMetadata && (
          <>
            <div className="align-top">API request details</div>
            <div className="text-xs space-y-1">
              {log.apiKeyMetadata.method && (
                <div>
                  <span className="font-medium">Method:</span> {log.apiKeyMetadata.method}
                </div>
              )}
              {log.apiKeyMetadata.endpoint && (
                <div>
                  <span className="font-medium">Endpoint:</span> {log.apiKeyMetadata.endpoint}
                </div>
              )}
              {log.apiKeyMetadata.ipAddress && (
                <div>
                  <span className="font-medium">IP address:</span> {log.apiKeyMetadata.ipAddress}
                </div>
              )}
              {log.apiKeyMetadata.userAgent && (
                <div>
                  <span className="font-medium">User agent:</span> {log.apiKeyMetadata.userAgent}
                </div>
              )}
            </div>
          </>
        )}

        <div className="align-top">Payload</div>
        <pre className="text-xs whitespace-pre-wrap">
          {log.payload && JSON.stringify(log.payload, null, 2)}
        </pre>

        <div className="align-top">Response</div>
        {response ? <pre className="text-xs whitespace-pre-wrap">{response}</pre> : <Dash />}
      </div>
    </div>
  );
}

export default function EventsLog({ integrationId, hideResend = false }) {
  const { token } = useAuth();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: integrationEventsKey(integrationId),
      queryFn: async ({ pageParam }) => {
        return fetchInfinite(
          `/integrations/${integrationId}/logs?${stringify({
            limit: 10,
            skip: pageParam * 10,
          })}`,
          token,
        );
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
      enabled: !!integrationId,
    });

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  const columns = useMemo(() => {
    const cols = [
      {
        id: 'expand',
        header: '',
        cell: ({ row }) => (
          <button
            type="button"
            aria-label={row.getIsExpanded() ? 'Collapse' : 'Expand'}
            onClick={row.getToggleExpandedHandler()}
            className="flex items-center"
          >
            {row.getIsExpanded() ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
        ),
        meta: { className: 'w-8' },
      },
      {
        accessorKey: 'deliveryStatus',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={getStatusBadgeAppearance(row.original.deliveryStatus)}>
            {row.original.deliveryStatus}
          </Badge>
        ),
      },
      {
        accessorKey: '_id',
        header: 'Event ID',
        cell: ({ row }) => row.original._id,
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'd MMM, yyyy, HH:mm'),
      },
      {
        accessorKey: 'retries',
        header: 'Retries',
        cell: ({ row }) => row.original.retries,
      },
    ];

    if (!hideResend) {
      cols.push({
        id: 'actions',
        header: '',
        cell: ({ row }) => <ResendButton log={row.original} />,
        meta: { className: 'text-right' },
      });
    }

    return cols;
  }, [hideResend]);

  if (isError) {
    return <div>Unable to show events. Please refresh or contact support.</div>;
  }

  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-y-2 py-12">
      <div className="text-gray-500 dark:text-muted-foreground">No events yet</div>
    </div>
  );

  return (
    <DataGrid
      columns={columns}
      data={flatData}
      isLoading={isLoading}
      paginationMode="infinite"
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={emptyState}
      renderSubRow={row => <EventDetails log={row.original} />}
    />
  );
}
